import { supabase } from './supabase.ts';

/**
 * Reusable sign-in, for pull-request previews only.
 *
 * A magic link is single-use by design, which is right for a real inbox and
 * wrong for a link that lives on a task somebody opens repeatedly — and worse
 * in a private window, where the session is thrown away on close so the link
 * is needed every single time. This is the alternative: the preview's throwaway
 * account has a password, the link carries it, and clicking it works as often
 * as you like.
 *
 * The safety property is that this cannot do anything in production, and not
 * merely that production does not use it: the address is compiled in from
 * `VITE_PREVIEW_LOGIN_EMAIL`, which only the preview workflow sets. In a
 * production build the constant is undefined, so this returns before it can
 * read the URL, let alone attempt a sign-in. That matters because the same
 * bundle is built from the same source — a runtime hostname check would be one
 * typo away from turning a URL fragment into a credential-stuffing endpoint on
 * the real app.
 */
const previewEmail = import.meta.env.VITE_PREVIEW_LOGIN_EMAIL as string | undefined;

export function isPreviewBuild(): boolean {
  return Boolean(previewEmail);
}

/**
 * Sign in from `#preview=…` if this is a preview build and the fragment
 * carries a password. Returns whether a session was established.
 *
 * Never throws: a preview that cannot sign itself in should fall through to
 * the ordinary sign-in screen, which now explains itself, rather than failing
 * to render at all.
 */
export async function consumePreviewLogin(): Promise<boolean> {
  if (!previewEmail) return false;

  const raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  if (!raw) return false;
  const password = new URLSearchParams(raw).get('preview');
  if (!password) return false;

  try {
    const { error } = await supabase.auth.signInWithPassword({ email: previewEmail, password });
    // Out of the address bar either way. It is a credential, and leaving it
    // there means it rides along into anything copied out of the URL — a
    // screenshot in a comment, a link pasted back into the task.
    history.replaceState(null, '', window.location.pathname + window.location.search);
    if (error) {
      console.warn('[rigg] preview sign-in failed:', error.message);
      return false;
    }
    return true;
  } catch (error) {
    console.warn('[rigg] preview sign-in failed:', (error as Error).message);
    return false;
  }
}
